import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { useParams, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import * as React from "react"
import { useCreatePackagePurchaseQR, useAvailablePackages } from "@/lib/api/package-purchase"
import { ActivationChoice, BankTransferInfo } from "@/src/types/package-purchase"
import { PAYMENT_FLAGS, SHOW_PAYMENT_SELECTOR, getDefaultPaymentMethod } from '@/lib/payment-flags'
import { AlertCircle, CheckCircle, Loader2, ArrowLeft, Package as PackageIcon, Calendar, Info, Zap, Copy, Banknote, CreditCard, Smartphone, Wallet, QrCode } from "lucide-react"
import { toast } from "sonner"
import { PromptPayQRDialog } from "@/src/components/payment/PromptPayQRDialog"
import { useAuth } from "@/src/contexts/AuthContext"
import { useDemoMode } from "@/src/contexts/DemoModeContext"
import { useQueryClient } from "@tanstack/react-query"
import axios from "axios"

export default function PurchasePackagePage() {
  const { packageId } = useParams<{ packageId: string }>()
  const navigate = useNavigate()
  const { user, refreshUser, refreshCredits } = useAuth()
  const { demoMode } = useDemoMode()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<'confirmation' | 'payment_instructions' | 'success'>('confirmation')
  const [bankTransfer, setBankTransfer] = useState<BankTransferInfo | null>(null)
  const [activationType, setActivationType] = useState<'immediate' | 'queued'>('immediate')
  const [currentPackageExpiry, setCurrentPackageExpiry] = useState<string | null>(null)
  // Activation choice states (for users with active paid package)
  const [activationChoice, setActivationChoice] = useState<ActivationChoice>('queued')
  const [immediateSwitchAccepted, setImmediateSwitchAccepted] = useState(false)
  const [effectiveChoice, setEffectiveChoice] = useState<ActivationChoice>('queued')
  const [paymentMethod, setPaymentMethod] = useState<'bank_transfer' | 'reservepay' | 'card' | 'mobile_banking' | 'true_money' | 'alipay' | 'wechat'>(getDefaultPaymentMethod())
  const [showQRDialog, setShowQRDialog] = useState(false)
  const [qrDialogActivationChoice, setQrDialogActivationChoice] = useState<'immediate' | 'queued'>('queued')

  // API hooks
  const createPaymentMutation = useCreatePackagePurchaseQR()
  const { data: packagesData, isLoading: isLoadingPackages } = useAvailablePackages()

  // Get package info from API data instead of mock
  const packageInfo = React.useMemo(() => {
    if (!packagesData?.availablePackages || !packageId) return undefined
    
    const pkg = packagesData.availablePackages.find(p => p.id === packageId)
    if (!pkg) return undefined
    
    // Transform to expected format
    return {
      id: pkg.id,
      name: pkg.name,
      displayName: pkg.display_name || pkg.name,
      monthlyPrice: parseFloat(pkg.price_monthly || '0'),
      includedCredits: pkg.included_credits,
      features: pkg.features || []
    }
  }, [packagesData, packageId])

  useEffect(() => {
    if (!isLoadingPackages && !packageInfo && packageId) {
      toast.error('ไม่พบแพ็กเกจที่เลือก')
      navigate('/settings/packages')
    }
  }, [packageInfo, packageId, isLoadingPackages, navigate])

  // No polling needed — admin will approve the slip manually

  // true when user has an active paid (non-starter) package
  const hasActivePaidPackage = React.useMemo(() => {
    const cur = packagesData?.currentPackage
    return !!cur && cur.name !== 'starter'
  }, [packagesData])

  const handleConfirmPurchase = async () => {
    if (!packageInfo) return

    try {
      // Determine effective activation choice
      const resolvedChoice: ActivationChoice = hasActivePaidPackage ? activationChoice : 'immediate'
      const accepted = resolvedChoice === 'immediate' ? immediateSwitchAccepted : false

      // Demo mode (UAT only): the backend completes the purchase without payment
      // verification for demo accounts, so skip every payment method screen.
      if (demoMode) {
        const demoResult = await createPaymentMutation.mutateAsync({
          packageId: packageInfo.id,
          activationChoice: resolvedChoice,
          immediateSwitchAccepted: accepted,
          consentVersion: '1.0'
        })

        if (demoResult.data.demo && demoResult.data.completed) {
          // New package permissions come with a fresh token
          if (demoResult.data.token) {
            localStorage.setItem('authToken', demoResult.data.token)
            axios.defaults.headers.common['Authorization'] = `Bearer ${demoResult.data.token}`
          }
          const demoEffective: ActivationChoice = demoResult.data.activationTiming.effectiveChoice ?? 'immediate'
          setEffectiveChoice(demoEffective)
          setActivationType(demoEffective)
          setStep('success')
          toast.success('โหมดเดโม: เปิดใช้งานแพ็กเกจเรียบร้อย')
          await queryClient.invalidateQueries({ queryKey: ['available-packages'] })
          await refreshUser()
          await refreshCredits()
          return
        }

        // Backend did not treat this account as a demo account (e.g. registered before
        // demo mode was on) - fall through to the normal bank transfer instructions.
        const normalEffective: ActivationChoice =
          demoResult.data.activationTiming.effectiveChoice ??
          (demoResult.data.activationTiming.isImmediate ? 'immediate' : 'queued')
        setEffectiveChoice(normalEffective)
        setActivationType(normalEffective)
        setBankTransfer(demoResult.data.bankTransfer ?? null)
        setCurrentPackageExpiry(demoResult.data.activationTiming.currentPackageExpiresAt || null)
        setStep('payment_instructions')
        toast.success('สร้างรายการชำระเงินสำเร็จ')
        return
      }

      if (paymentMethod === 'reservepay') {
        // Open in-app QR dialog (PromptPay — Custom UI flow)
        setQrDialogActivationChoice(resolvedChoice)
        setShowQRDialog(true)
        return
      }

      if (paymentMethod === 'card') {
        // Navigate to full-page CARD flow
        const amount = packageInfo.monthlyPrice
        navigate(`/payment/reservepay?amount=${amount}&type=package&packageId=${packageInfo.id}&method=card`)
        return
      }

      if (paymentMethod === 'mobile_banking') {
        // Navigate to full-page Mobile Banking flow
        const amount = packageInfo.monthlyPrice
        navigate(`/payment/reservepay?amount=${amount}&type=package&packageId=${packageInfo.id}&method=mobile_banking`)
        return
      }

      if (paymentMethod === 'true_money') {
        const amount = packageInfo.monthlyPrice
        navigate(`/payment/reservepay?amount=${amount}&type=package&packageId=${packageInfo.id}&method=true_money`)
        return
      }

      if (paymentMethod === 'alipay') {
        const amount = packageInfo.monthlyPrice
        navigate(`/payment/reservepay?amount=${amount}&type=package&packageId=${packageInfo.id}&method=alipay`)
        return
      }

      if (paymentMethod === 'wechat') {
        const amount = packageInfo.monthlyPrice
        navigate(`/payment/reservepay?amount=${amount}&type=package&packageId=${packageInfo.id}&method=wechat`)
        return
      }

      const result = await createPaymentMutation.mutateAsync({
        packageId: packageInfo.id,
        activationChoice: resolvedChoice,
        immediateSwitchAccepted: accepted,
        consentVersion: '1.0'
      })

      // Store effective choice returned from backend
      const backendEffective = result.data.activationTiming.effectiveChoice
      const resolvedEffective: ActivationChoice =
        backendEffective ?? (result.data.activationTiming.isImmediate ? 'immediate' : 'queued')

      setEffectiveChoice(resolvedEffective)
      setActivationType(resolvedEffective)
      setBankTransfer(result.data.bankTransfer ?? null)
      setCurrentPackageExpiry(result.data.activationTiming.currentPackageExpiresAt || null)
      setStep('payment_instructions')
      toast.success('สร้างรายการชำระเงินสำเร็จ')

    } catch (error: any) {
      if (error.code === 'PENDING_PACKAGE_EXISTS') {
        toast.error('คุณมีแพ็กเกจรออยู่แล้ว กรุณารอจนกว่าจะเริ่มใช้งานก่อนซื้อใหม่')
      } else if (error.code === 'SAME_PACKAGE') {
        toast.error('คุณใช้แพ็กเกจนี้อยู่แล้ว')
      } else {
        toast.error(`เกิดข้อผิดพลาด: ${error.message}`)
      }
    }
  }

  const handlePaymentSuccess = () => {
    // Set step to success to show completion message
    setStep('success')
    // Show success notification
    toast.success("✅ ยืนยันการชำระเงินแล้ว", {
      description: "ขอบคุณที่แจ้งยืนยัน! เจ้าหน้าที่จะตรวจสอบการชำระเงินและอัพเกรดแพ็กเกจให้ภายใน 24 ชั่วโมง",
    })
  }

  if (!packageInfo) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    )
  }

  // Step 1: Confirmation
  if (step === 'confirmation') {
    return (
      <>
        <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/settings/packages')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              ย้อนกลับ
            </Button>
            <h1 className="text-2xl font-bold">ยืนยันการซื้อแพ็กเกจ</h1>
          </div>

          <div className="max-w-2xl mx-auto space-y-6">
            {/* Package Details Card */}
            <Card className="p-6">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <PackageIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold">{packageInfo.displayName}</h2>
                    <p className="text-3xl font-bold text-primary mt-2">
                      ฿{packageInfo.monthlyPrice.toLocaleString()}
                      <span className="text-base text-gray-600 font-normal">/เดือน</span>
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">สิ่งที่คุณจะได้รับ:</h3>
                  <ul className="space-y-2">
                    {packageInfo.features.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Activation Timing Info — shown only for starter users (no choice needed) */}
                {!hasActivePaidPackage && (
                <div className="border-t pt-4">
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-900">
                        ⚡ เริ่มใช้งานทันที
                      </p>
                    </div>
                  </div>
                </div>
                )}

                {/* Activation Choice Selector — shown only when user has active paid package */}
                {hasActivePaidPackage && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-semibold text-gray-900">เลือกเวลาเริ่มใช้งานแพ็กเกจใหม่</h3>

                  <RadioGroup
                    value={activationChoice}
                    onValueChange={(val) => {
                      setActivationChoice(val as ActivationChoice)
                      if (val === 'queued') setImmediateSwitchAccepted(false)
                    }}
                    className="space-y-3"
                  >
                    {/* Queued option (recommended) */}
                    <div className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      activationChoice === 'queued'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                      onClick={() => {
                        setActivationChoice('queued')
                        setImmediateSwitchAccepted(false)
                      }}
                    >
                      <RadioGroupItem value="queued" id="queued" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="queued" className="font-semibold text-gray-900 cursor-pointer">
                          📋 รอจนกว่าแพ็กเกจปัจจุบันจะหมดอายุ{' '}
                          <span className="text-xs text-blue-600 font-medium">[แนะนำ]</span>
                        </Label>
                        <p className="text-sm text-gray-600 mt-1">
                          ใช้งาน {packagesData?.currentPackage?.name} ต่อได้จนหมดอายุ
                          {packagesData?.currentPackage?.days_remaining
                            ? ` (อีก ${packagesData.currentPackage.days_remaining} วัน)`
                            : ''}
                          {' '}จากนั้นแพ็กเกจใหม่จะเริ่มโดยอัตโนมัติ
                        </p>
                      </div>
                    </div>

                    {/* Immediate option */}
                    <div className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      activationChoice === 'immediate'
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                      onClick={() => setActivationChoice('immediate')}
                    >
                      <RadioGroupItem value="immediate" id="immediate" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="immediate" className="font-semibold text-gray-900 cursor-pointer flex items-center gap-2">
                          <Zap className="w-4 h-4 text-orange-500" />
                          เริ่มใช้งานทันทีหลังอนุมัติ
                        </Label>
                        <p className="text-sm text-gray-600 mt-1">
                          แพ็กเกจปัจจุบันจะถูกยกเลิก และเครดิตคงเหลือจากแพ็กเกจเก่าจะหมดอายุ
                        </p>
                      </div>
                    </div>
                  </RadioGroup>

                  {/* Consent checkbox — shown only when immediate is selected */}
                  {activationChoice === 'immediate' && (
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-300 space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-orange-900">
                          <p className="font-semibold mb-1">⚠️ สิ่งที่จะเกิดขึ้นเมื่อสลับทันที:</p>
                          <ul className="list-disc list-inside space-y-1 text-orange-800">
                            <li>แพ็กเกจ {packagesData?.currentPackage?.name} ปัจจุบันจะถูกยกเลิกทันที</li>
                            <li>เครดิตคงเหลือที่ได้จากแพ็กเกจเดิมจะหมดอายุ (เครดิตที่ซื้อเพิ่มยังคงอยู่)</li>
                            <li>แพ็กเกจ {packageInfo?.displayName} จะเริ่มใช้งานทันทีหลังอนุมัติ</li>
                            <li>ไม่สามารถย้อนกลับได้</li>
                          </ul>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 pt-1">
                        <Checkbox
                          id="immediateSwitchConsent"
                          checked={immediateSwitchAccepted}
                          onCheckedChange={(checked) => setImmediateSwitchAccepted(checked === true)}
                          className="mt-0.5"
                        />
                        <Label
                          htmlFor="immediateSwitchConsent"
                          className="text-sm font-medium text-orange-900 cursor-pointer leading-5"
                        >
                          ฉันเข้าใจและยอมรับเงื่อนไขการสลับแพ็กเกจทันที รวมถึงการสูญเสียเครดิตคงเหลือจากแพ็กเกจเดิม
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Important Notice */}
                <div className="border-t pt-4">
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900">⚠️ นโยบายการคืนเงิน</p>
                      <p className="text-amber-800 mt-1">
                        การซื้อแพ็กเกจไม่สามารถยกเลิกหรือขอคืนเงินได้ กรุณาตรวจสอบรายละเอียดก่อนยืนยันการชำระเงิน
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment method selector — shown only when both methods are enabled */}
                {demoMode && (
                <div className="border-t pt-4">
                  <div role="status" className="p-4 bg-amber-50 rounded-lg border border-amber-300 text-sm text-amber-900">
                    <span className="font-semibold">โหมดเดโม:</span> ระบบจะเปิดใช้งานแพ็กเกจทันทีโดยไม่ตรวจสอบการชำระเงิน
                  </div>
                </div>
                )}

                {!demoMode && SHOW_PAYMENT_SELECTOR && (
                <div className="border-t pt-4">
                  <p className="font-semibold mb-3">วิธีการชำระเงิน</p>
                  <div className={`grid gap-3 ${(PAYMENT_FLAGS.reservepay ? 1 : 0) + (PAYMENT_FLAGS.bankTransfer ? 1 : 0) + (PAYMENT_FLAGS.card ? 1 : 0) + (PAYMENT_FLAGS.mobileBanking ? 1 : 0) >= 4 ? 'grid-cols-3' : (PAYMENT_FLAGS.reservepay ? 1 : 0) + (PAYMENT_FLAGS.bankTransfer ? 1 : 0) + (PAYMENT_FLAGS.card ? 1 : 0) + (PAYMENT_FLAGS.mobileBanking ? 1 : 0) >= 3 ? 'grid-cols-3' : PAYMENT_FLAGS.reservepay && PAYMENT_FLAGS.bankTransfer ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {PAYMENT_FLAGS.reservepay && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('reservepay')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'reservepay'
                          ? 'border-[#7FB069] bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Zap className={`w-6 h-6 ${paymentMethod === 'reservepay' ? 'text-[#7FB069]' : 'text-gray-400'}`} />
                      <span className={`font-semibold text-sm ${paymentMethod === 'reservepay' ? 'text-[#7FB069]' : 'text-gray-600'}`}>
                        PromptPay อัตโนมัติ
                      </span>
                      <span className="text-xs text-gray-400 text-center">สแกน QR จ่ายทันที ยืนยันอัตโนมัติ</span>
                      {paymentMethod === 'reservepay' && (
                        <span className="text-xs bg-[#7FB069] text-white px-2 py-0.5 rounded-full">แนะนำ</span>
                      )}
                    </button>
                    )}
                    {PAYMENT_FLAGS.bankTransfer && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('bank_transfer')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'bank_transfer'
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Banknote className={`w-6 h-6 ${paymentMethod === 'bank_transfer' ? 'text-blue-500' : 'text-gray-400'}`} />
                      <span className={`font-semibold text-sm ${paymentMethod === 'bank_transfer' ? 'text-blue-600' : 'text-gray-600'}`}>
                        โอนเงินผ่านธนาคาร
                      </span>
                      <span className="text-xs text-gray-400 text-center">โอนแล้วส่งสลิปให้เจ้าหน้าที่</span>
                    </button>
                    )}
                    {PAYMENT_FLAGS.card && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'card'
                          ? 'border-purple-400 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <CreditCard className={`w-6 h-6 ${paymentMethod === 'card' ? 'text-purple-500' : 'text-gray-400'}`} />
                      <span className={`font-semibold text-sm ${paymentMethod === 'card' ? 'text-purple-600' : 'text-gray-600'}`}>
                        บัตรเครดิต/เดบิต
                      </span>
                      <span className="text-xs text-gray-400 text-center">ชำระผ่านบัตร ปลอดภัย 3D Secure</span>
                    </button>
                    )}
                    {PAYMENT_FLAGS.mobileBanking && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('mobile_banking')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'mobile_banking'
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Smartphone className={`w-6 h-6 ${paymentMethod === 'mobile_banking' ? 'text-orange-500' : 'text-gray-400'}`} />
                      <span className={`font-semibold text-sm ${paymentMethod === 'mobile_banking' ? 'text-orange-600' : 'text-gray-600'}`}>
                        Mobile Banking
                      </span>
                      <span className="text-xs text-gray-400 text-center">ชำระผ่านแอปธนาคาร BBL / TTB</span>
                    </button>
                    )}
                    {PAYMENT_FLAGS.trueMoney && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('true_money')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'true_money'
                          ? 'border-red-400 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Wallet className={`w-6 h-6 ${paymentMethod === 'true_money' ? 'text-red-500' : 'text-gray-400'}`} />
                      <span className={`font-semibold text-sm ${paymentMethod === 'true_money' ? 'text-red-600' : 'text-gray-600'}`}>
                        TrueMoney Wallet
                      </span>
                      <span className="text-xs text-gray-400 text-center">ชำระผ่าน TrueMoney Wallet</span>
                    </button>
                    )}
                    {PAYMENT_FLAGS.alipay && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('alipay')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'alipay'
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <QrCode className={`w-6 h-6 ${paymentMethod === 'alipay' ? 'text-blue-500' : 'text-gray-400'}`} />
                      <span className={`font-semibold text-sm ${paymentMethod === 'alipay' ? 'text-blue-600' : 'text-gray-600'}`}>
                        Alipay
                      </span>
                      <span className="text-xs text-gray-400 text-center">สแกน QR จ่ายผ่าน Alipay</span>
                    </button>
                    )}
                    {PAYMENT_FLAGS.wechat && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('wechat')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        paymentMethod === 'wechat'
                          ? 'border-emerald-400 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <QrCode className={`w-6 h-6 ${paymentMethod === 'wechat' ? 'text-emerald-500' : 'text-gray-400'}`} />
                      <span className={`font-semibold text-sm ${paymentMethod === 'wechat' ? 'text-emerald-600' : 'text-gray-600'}`}>
                        WeChat Pay
                      </span>
                      <span className="text-xs text-gray-400 text-center">สแกน QR จ่ายผ่าน WeChat</span>
                    </button>
                    )}
                  </div>
                </div>
                )}
              </div>
            </Card>

            {/* Confirm Button */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/settings/packages')}
                className="px-10 py-6 text-lg font-semibold"
              >
                ยกเลิก
              </Button>
              <Button
                onClick={handleConfirmPurchase}
                disabled={
                  createPaymentMutation.isPending ||
                  (hasActivePaidPackage && activationChoice === 'immediate' && !immediateSwitchAccepted)
                }
                className="px-10 py-6 text-lg font-bold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {createPaymentMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    กำลังสร้างรายการ...
                  </>
                ) : demoMode ? (
                  'ยืนยันการซื้อ (โหมดเดโม)'
                ) : paymentMethod === 'reservepay' ? (
                  '⚡ ชำระด้วย PromptPay'
                ) : (
                  'ยืนยันและดูรายละเอียดการโอนเงิน'
                )}
              </Button>
            </div>
          </div>
        </div>

        </DashboardLayout>

      {packageInfo && (
        <PromptPayQRDialog
          open={showQRDialog}
          onOpenChange={setShowQRDialog}
          amount={packageInfo.monthlyPrice}
          customerEmail={user?.email}
          customerMobile={user?.mobile}
          customerName={user?.shopName}
          type="package"
          packageId={packageInfo.id}
          activationChoice={qrDialogActivationChoice}
          onSuccess={() => {
            setShowQRDialog(false)
            toast.success('ชำระเงินสำเร็จ! แพ็กเกจของคุณจะเริ่มใช้งานเร็วๆ นี้')
            navigate('/settings/packages')
          }}
          onFail={() => toast.error('การชำระเงิน PromptPay ไม่สำเร็จ กรุณาลองใหม่')}
        />
      )}
    </>
    )
  }
  if (step === 'payment_instructions') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStep('confirmation')
                setBankTransfer(null)
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              ย้อนกลับ
            </Button>
            <h1 className="text-2xl font-bold">รายละเอียดการโอนเงิน</h1>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <Card className="p-8">
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold text-primary">
                    โอนเงินเพื่อชำระแพ็กเกจ
                  </h2>
                  <p className="text-gray-600">
                    {packageInfo.displayName} — ฿{packageInfo.monthlyPrice.toLocaleString()}
                  </p>
                </div>

                {/* Bank Account Details */}
                {bankTransfer && (
                  <div className="p-5 bg-green-50 rounded-xl border-2 border-green-300 space-y-3">
                    <p className="font-semibold text-green-900 text-lg">🏦 บัญชีปลายทาง</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">ธนาคาร</span>
                        <span className="font-semibold text-gray-900">{bankTransfer.bankName}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">เลขที่บัญชี</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900 text-base tracking-wider">{bankTransfer.accountNumber}</span>
                          <button
                            onClick={() => { navigator.clipboard.writeText(bankTransfer.accountNumber); toast.success('คัดลอกเลขบัญชีแล้ว') }}
                            className="text-primary hover:text-primary/70 transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">ชื่อบัญชี</span>
                        <span className="font-semibold text-gray-900">{bankTransfer.accountName}</span>
                      </div>
                      <div className="border-t border-green-200 pt-2 flex justify-between">
                        <span className="text-gray-600">จำนวนเงินที่ต้องโอน</span>
                        <span className="font-bold text-green-700 text-lg">฿{bankTransfer.amount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reference Number */}
                {bankTransfer && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-300 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-800 font-medium">เลขอ้างอิง (ระบุในช่องหมายเหตุ)</p>
                      <p className="font-bold text-amber-900 text-lg tracking-widest">{bankTransfer.referenceNumber}</p>
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(bankTransfer.referenceNumber); toast.success('คัดลอกเลขอ้างอิงแล้ว') }}
                      className="text-amber-700 hover:text-amber-900 transition-colors ml-4"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Step-by-step instructions */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-900">📱 ขั้นตอนการชำระเงิน:</p>
                      <ol className="text-sm text-blue-800 mt-2 space-y-1 list-decimal list-inside">
                        <li>โอนเงินตามจำนวนและบัญชีที่ระบุข้างบน</li>
                        <li>ระบุเลขอ้างอิงในช่องหมายเหตุการโอนเงิน</li>
                        <li>บันทึกหรือถ่ายภาพสลิปการโอนเงิน</li>
                        <li>ส่งสลิปเข้า LINE Official Account เพื่อให้เจ้าหน้าที่ตรวจสอบ</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Activation Type Info */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex gap-3">
                    <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-purple-900">
                        {activationType === 'immediate'
                          ? '⚡ เริ่มใช้งานทันทีหลังอนุมัติ'
                          : '📋 เริ่มใช้งานเมื่อแพ็กเกจปัจจุบันหมดอายุ'}
                      </p>
                      {activationType === 'queued' && currentPackageExpiry && (
                        <p className="text-sm text-purple-800 mt-1">
                          จะเริ่มใช้งานประมาณวันที่ {new Date(currentPackageExpiry).toLocaleDateString('th-TH')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('confirmation')
                  setBankTransfer(null)
                }}
              >
                ยกเลิก
              </Button>
              <Button
                onClick={handlePaymentSuccess}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                ✅ โอนเงินแล้ว / แจ้งชำระเงิน
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  // Step 3: Success
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">ชำระเงินสำเร็จ</h1>

        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="p-8">
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-green-600 mb-2">
                  ยืนยันการชำระเงินสำเร็จ!
                </h2>
                <p className="text-gray-600">
                  คุณได้ชำระเงินสำหรับแพ็กเกจ {packageInfo.displayName} เรียบร้อยแล้ว
                </p>
              </div>

              <div className="border-t pt-6 space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-600">แพ็กเกจ:</span>
                  <span className="font-semibold">{packageInfo.displayName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ราคา:</span>
                  <span className="font-semibold">฿{packageInfo.monthlyPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">เครดิตที่ได้รับ:</span>
                  <span className="font-semibold">{packageInfo.includedCredits.toLocaleString()} เครดิต</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">สถานะ:</span>
                  <span className="font-semibold text-green-600">
                    {effectiveChoice === 'immediate' ? 'เริ่มใช้งานแล้ว' : 'รออนุมัติ'}
                  </span>
                </div>
              </div>

              {effectiveChoice === 'queued' && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-left">
                  <p className="text-sm text-blue-900">
                    <strong>📋 หมายเหตุ:</strong> แพ็กเกจนี้จะเริ่มใช้งานเมื่อแพ็กเกจปัจจุบันของคุณหมดอายุ
                  </p>
                </div>
              )}
            </div>
          </Card>

          <div className="flex justify-center">
            <Button onClick={() => navigate('/settings/packages')}>
              กลับไปหน้าแพ็กเกจ
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
